import { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import Modal from "../../components/modal/Modal";
import { GET_INSPECTIONS } from "../../graphql/queries";
import { downloadExport } from "../../helpers/exportDownload";
import { statusClass } from "../../helpers/utils";
import { getFuzzyDate } from "../../helpers/dates";

export type RecordsFilter = {
   filter?: string;
   status?: string[];
   month?: string;
   defectsView?: "all" | "with_defects" | "rectified";
   trackerCategory?: "no_defects" | "rectified" | "pending" | "overdue";
   transporterId?: number;
};

function setupName(s: any) {
   if (!s) return "";
   const f = s.fields?.find((x: any) => x.name === "name");
   return f?.value || "";
}

export default function RecordsModal({
   title,
   filter,
   onClose,
}: {
   title: string;
   filter: RecordsFilter;
   onClose: () => void;
}) {
   const [getInspections, { loading }] = useLazyQuery(GET_INSPECTIONS, {
      fetchPolicy: "network-only",
   });
   const [data, setData] = useState<any[]>([]);
   const [totalRecords, setTotalRecords] = useState(0);
   const [exporting, setExporting] = useState(false);

   useEffect(() => {
      getInspections({
         variables: {
            filter: filter.filter || "",
            status: filter.status,
            month: filter.month,
            defectsView: filter.defectsView,
            trackerCategory: filter.trackerCategory,
            transporterId: filter.transporterId,
            page: 1,
            limit: 1000,
         },
      }).then((res) => {
         const r = res?.data?.getAllInspections;
         setData(r?.records || []);
         setTotalRecords(r?.totalRecords ?? 0);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [
      filter.filter,
      filter.status?.join(","),
      filter.month,
      filter.defectsView,
      filter.trackerCategory,
      filter.transporterId,
   ]);

   async function handleExport() {
      setExporting(true);
      try {
         await downloadExport("inspections", {
            filter: filter.filter || "",
            status: filter.status,
            month: filter.month,
            defects_view: filter.defectsView,
            tracker_category: filter.trackerCategory,
            transporter_id: filter.transporterId,
         });
      } finally {
         setExporting(false);
      }
   }

   return (
      <Modal
         size="xl"
         header={
            <div className="flex items-center justify-between gap-4 w-full">
               <b>{title}</b>
               <span className="text-xs text-slate-500">
                  {loading ? "Loading…" : `${totalRecords} record(s)`}
               </span>
            </div>
         }
         handleClose={onClose}
      >
         <div className="flex justify-end px-2 pb-2">
            <button
               type="button"
               onClick={handleExport}
               disabled={exporting || data.length === 0}
               className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 px-2 py-1 rounded text-white text-sm"
               title="Export these records to Excel"
            >
               {exporting ? "Exporting..." : "Export to Excel"}
            </button>
         </div>
         <div className="w-full overflow-y-auto" style={{ maxHeight: "70vh" }}>
            <table className="table border-collapse border border-slate-400 table-auto w-full text-left text-xs">
               <thead className="bg-slate-100 sticky top-0">
                  <tr>
                     <th className="border border-slate-300 px-2">#</th>
                     <th className="border border-slate-300 px-2">Transporter/Customer</th>
                     <th className="border border-slate-300 px-2">Site</th>
                     <th className="border border-slate-300 px-2 whitespace-nowrap">Inspected</th>
                     <th className="border border-slate-300 px-2 whitespace-nowrap">Reg. No.</th>
                     <th className="border border-slate-300 px-2">License No</th>
                     <th className="border border-slate-300 px-2">Status</th>
                     <th className="border border-slate-300 px-2">Overdue</th>
                  </tr>
               </thead>
               <tbody>
                  {loading ? (
                     <tr><td colSpan={8} className="px-2 text-orange-400 py-2">Loading…</td></tr>
                  ) : data.length === 0 ? (
                     <tr><td colSpan={8} className="px-2 py-2">No records.</td></tr>
                  ) : (
                     data.map((i: any) => {
                        const overdue = Math.max(0, (i.defectsAge || 0) - 7);
                        return (
                           <tr key={i.id} className={statusClass(i.status)}>
                              <td className="border border-slate-300 px-2">{i.id}</td>
                              <td className="border border-slate-300 px-2">
                                 {setupName(i.transporter) || setupName(i.customer)}
                              </td>
                              <td className="border border-slate-300 px-2">{setupName(i.site)}</td>
                              <td className="border border-slate-300 px-2">{getFuzzyDate(new Date(i.createdAt))}</td>
                              <td className="border border-slate-300 px-2">{i.vehicleReg}</td>
                              <td className="border border-slate-300 px-2">{i.licenseNo}</td>
                              <td className="border border-slate-300 px-2">{i.status}</td>
                              <td className="border border-slate-300 px-2 whitespace-nowrap">{overdue} day(s)</td>
                           </tr>
                        );
                     })
                  )}
               </tbody>
            </table>
         </div>
      </Modal>
   );
}
